import * as React from 'react';
import { DAY_OF_WEEKS, ISODate, ISODateTime } from '../../../../commonv3/types/dates';
import { action, observable, reaction } from 'mobx';
import { CalendarViewState } from './states/CalendarViewState';
import classNames from 'classnames';
import ChevronRight from './cal-chevron-right.svg';
import ChevronLeft from './cal-chevron-left.svg';
import './calendarView.module.scss';
import { observer } from 'mobx-react';
import moment from 'moment-timezone';
import _ from 'lodash';
import { HexColorCode } from '../../../../commonv3/types/image';

type Props = {
    /**
     * The first date
     * It doesn't allow scrolling back from this date. (inclusive)
     */
    startDate: moment.Moment;
    /**
     * The end date ths ca
     * It doesn't allow scrolling forward from this date. (exclusive)
     */
    endDate: moment.Moment;
    /**
     * Selected date, highlighted
     */
    selectedDate: ISODate | moment.Moment;
    /**
     * The list of clickable dates
     * May contain duplicates.
     */
    clickables: ISODateTime[];
    /**
     * List of highlighted dates
     * If set, it shows small dots at the top left of the cell
     */
    highlights?: (ISODate | moment.Moment)[];
    /**
     * The color of highlight dot
     * Default : Pink 1
     */
    highlightColor?: HexColorCode;
    /**
     * The callback called when any of the clickable dates were clicked
     * @param datetime
     */
    onClick: (datetime: ISODate) => any;

    /**
     * Called once on initialization, and whenever the displayed month changes.
     * @param month
     */
    onMonthChange: (month: moment.Moment) => any;
};

/**
 * A calendar view that makes
 */
@observer
export class CalendarView extends React.Component<Props> {
    @observable viewState = new CalendarViewState();
    componentDidMount(): void {
        this.viewState.init(this.props.startDate, this.props.endDate);
        this.viewState.initClickableDates(this.props.clickables);
        this.props.onMonthChange(this.viewState.currentMonth);
        reaction(() => this.viewState.currentMonth, (currentMonth) => this.props.onMonthChange(currentMonth));
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (
            this.props.startDate.format('YYYY-MM-DD') !== prevProps.startDate.format('YYYY-MM-DD') ||
            this.props.endDate.format('YYYY-MM-DD') !== prevProps.endDate.format('YYYY-MM-DD')
        ) {
            this.viewState.init(this.props.startDate, this.props.endDate);
        }
        if (!_.isEqual(this.props.clickables, prevProps.clickables)) {
            this.viewState.initClickableDates(this.props.clickables);
        }
    }

    @action.bound
    handleClick(date) {
        const clickedMonth = moment(date).startOf('month');
        if (!this.viewState.currentMonth.isSame(clickedMonth)) {
            this.viewState.currentMonth = clickedMonth;
        }
        if (this.props.onClick) {
            this.props.onClick(date);
        }
    }

    render() {
        const {
            currentMonth,
            currentRows,
            clickableDates,
            hasNextMonth,
            nextMonth,
            hasPrevMonth,
            prevMonth,
        } = this.viewState;
        const { selectedDate, highlights = [], highlightColor = '#D6A0A0' } = this.props;
        if (!currentMonth) {
            return null;
        }
        const selectedDateStr = selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : null;
        return (
            <div styleName="calendar">
                <div styleName="header">
                    <div
                        id="prev-month-button"
                        styleName={classNames('chevron-wrapper', { disabled: !hasPrevMonth })}
                        onClick={prevMonth}
                    >
                        <img styleName={classNames('chevron', { disabled: !hasPrevMonth })} src={ChevronLeft} />
                    </div>
                    <span
                        styleName="header-text"
                        data-year-month={currentMonth.format('YYYY-MM')}
                        data-month={currentMonth.format('M')}
                        data-year={currentMonth.format('YYYY')}
                    >
                        {currentMonth.format('M')}月
                    </span>
                    <div
                        id="next-month-button"
                        styleName={classNames('chevron-wrapper', { disabled: !hasNextMonth })}
                        onClick={nextMonth}
                    >
                        <img styleName={classNames('chevron', { disabled: !hasNextMonth })} src={ChevronRight} />
                    </div>
                </div>
                <div styleName="main">
                    <div styleName="calendar-header">
                        {DAY_OF_WEEKS.map((dow) => (
                            <div key={dow} styleName="calendar-header-item">
                                {dow}
                            </div>
                        ))}
                    </div>
                    <div styleName="calendar-body">
                        {currentRows.map((row, idx) => (
                            <div styleName="calendar-row" key={idx}>
                                {row.map((day) => {
                                    const date = day.format('YYYY-MM-DD');
                                    const selected = date === selectedDateStr;
                                    const clickable = clickableDates.has(date);
                                    return (
                                        <div
                                            key={date}
                                            className="calendar-row-item"
                                            styleName={classNames('calendar-row-item', {
                                                clickable: clickable,
                                                selected: selected,
                                            })}
                                            onClick={clickable ? () => this.handleClick(date) : undefined}
                                            data-date={date}
                                            data-clickable={clickable ? 'true' : 'false'}
                                        >
                                            {highlights.find((m) => moment(m).isSame(day)) && (
                                                <div styleName="highlight-dot" style={{ background: highlightColor }} />
                                            )}
                                            {day.format('D')}
                                            {!clickable && <div styleName="cross-bar" />}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }
}
